import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetAccountModulesQuery, useGetProjectQuery } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useAppSelector } from "fond/utils/hooks";
import { accountModuleCheck, Actions } from "fond/utils/permissions";
import { isCollaborationProject } from "fond/utils/project";

interface UseCanIConstructionPrintExportReturn {
  /**
   * Whether the user can create construction print exports for the current project. Rules:
   * - The project must be a Collaboration project
   * - The account must have the "Collaboration Construction Print Export" module enabled
   * - The "collaboration_construction_print_export" feature flag must be enabled
   */
  canCreateConstructionPrints: boolean;
  /**
   * Whether any of the underlying queries are still loading.
   */
  isLoading: boolean;
}

export const useCanIConstructionPrintExport = (): UseCanIConstructionPrintExportReturn => {
  const projectId = useAppSelector((state) => state.project.projectId);
  const { data: project, isLoading: isLoadingProject } = useGetProjectQuery(projectId ?? skipToken);
  const { data: accountModules, isLoading: isLoadingAccountModules } = useGetAccountModulesQuery(project?.Account.ID ?? skipToken);
  const { value: isFlagEnabled, loading: isLoadingFeatureFlag } = useFeatureFlag("collaboration_construction_print_export");

  const isLoading = isLoadingProject || isLoadingAccountModules || isLoadingFeatureFlag;

  return useMemo(
    () => ({
      canCreateConstructionPrints:
        !isLoading &&
        isCollaborationProject(project) &&
        accountModuleCheck(accountModules, Actions.COLLABORATION_CONSTRUCTION_PRINT_EXPORT) &&
        isFlagEnabled,
      isLoading,
    }),
    [isLoading, project, accountModules, isFlagEnabled]
  );
};
